import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Package, Calendar, MapPin, Phone, Mail, User, LogOut, Plus,
  Clock, CheckCircle, Truck, AlertTriangle, Users, X, RefreshCw,
  Eye, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  MessageSquare, Loader2, ToggleLeft, ToggleRight,
  Trash2, Home, Building2, Hash, FileText, DollarSign, History,
  Shield, Edit2, Save, Settings, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom fetch that preserves response body for error cases
// The Emergent script patches window.fetch and reads response.text() for error logging
// This consumes the body before our code can access it
const safeFetch = async (url, options) => {
  // Store reference to the original fetch (before any patches)
  // Using XMLHttpRequest as a reliable alternative
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options?.method || 'GET', url, true);
    
    // Set headers
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    
    xhr.onload = function() {
      // Create a Response-like object
      const responseObj = {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        text: () => Promise.resolve(xhr.responseText),
        json: () => Promise.resolve(JSON.parse(xhr.responseText)),
        headers: new Headers()
      };
      resolve(responseObj);
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(options?.body);
  });
};

// Safe response parser
const safeParseResponse = async (response) => {
  try {
    const text = await response.text();
    if (text) {
      try {
        return JSON.parse(text);
      } catch {
        return { detail: text };
      }
    }
    return { detail: 'Empty response' };
  } catch (err) {
    console.error('Response parsing error:', err);
    return { detail: 'Unable to process response' };
  }
};

// External status colors for client view
const STATUS_COLORS = {
  booking_received: { bg: '#F1F5F9', text: '#64748B', label: 'Booking Received' },
  scheduled: { bg: '#DBEAFE', text: '#2563EB', label: 'Scheduled' },
  on_the_way: { bg: '#FEF3C7', text: '#D97706', label: 'On the Way' },
  collected: { bg: '#D1FAE5', text: '#059669', label: 'Collected' },
  completed: { bg: '#DCFCE7', text: '#16A34A', label: 'Completed' },
  cancelled: { bg: '#FEE2E2', text: '#DC2626', label: 'Cancelled' }
};

const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('login'); // login, set-password, dashboard
  const [error, setError] = useState('');
  
  // Login state
  const [loginForm, setLoginForm] = useState({ mobile: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Set password state
  const [passwordForm, setPasswordForm] = useState({ mobile: '', otp: '', password: '', confirmPassword: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Dashboard state
  const [client, setClient] = useState(null);
  const [quota, setQuota] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Activity history
  const [activityHistory, setActivityHistory] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Booking-specific activity
  const [bookingActivity, setBookingActivity] = useState({ status_history: [], activity_logs: [] });
  const [loadingBookingActivity, setLoadingBookingActivity] = useState(false);
  
  // Add user modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', mobile: '', email: '', role: 'installer' });
  const [isAddingUser, setIsAddingUser] = useState(false);
  
  // Photo state
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // New Booking Modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStep, setBookingStep] = useState(0); // 0: site details, 1: confirm
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    siteAddress: '',
    customerName: '',
    customerMobile: '',
    siteNotes: '',
    hasPallets: false,
    palletCount: '',
    sitePhotos: [] // Array of uploaded photo URLs
  });
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // Address autocomplete for booking
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const addressDebounceRef = React.useRef(null);
  const sessionTokenRef = React.useRef(crypto.randomUUID());

  // Photo helper functions
  const getPhotoUrl = (photo) => {
    if (!photo) return '';
    if (typeof photo === 'object' && photo.url) {
      const url = photo.url;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('/api/')) return `${API_URL}${url}`;
      return url;
    }
    if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
    if (photo.startsWith('/api/')) return `${API_URL}${photo}`;
    return photo;
  };

  const getPhotoFilename = (photo) => {
    if (!photo) return '';
    if (typeof photo === 'object' && photo.filename) return photo.filename;
    if (typeof photo === 'string') {
      const parts = photo.split('/');
      return parts[parts.length - 1] || 'Photo';
    }
    return 'Photo';
  };

  const getPhotoViewLink = (photo) => {
    if (!photo) return null;
    if (typeof photo === 'object' && photo.view_link) return photo.view_link;
    return null;
  };

  // Address autocomplete for booking
  const handleAddressSearch = async (query) => {
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    
    addressDebounceRef.current = setTimeout(async () => {
      setIsLoadingAddresses(true);
      try {
        const response = await safeFetch(
          `${API_URL}/api/places/autocomplete?input=${encodeURIComponent(query)}&session_token=${sessionTokenRef.current}`
        );
        if (response.ok) {
          const data = await safeParseResponse(response);
          setAddressSuggestions(data.predictions || []);
          setShowAddressSuggestions(true);
        }
      } catch (err) {
        console.error('Address search failed:', err);
      } finally {
        setIsLoadingAddresses(false);
      }
    }, 300);
  };

  const selectAddress = async (prediction) => {
    setBookingForm(prev => ({ ...prev, siteAddress: prediction.description }));
    setShowAddressSuggestions(false);
    sessionTokenRef.current = crypto.randomUUID();
  };

  // Photo upload handler for booking
  const handleBookingPhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploadingPhoto(true);
    setBookingError('');
    
    try {
      for (const file of files) {
        // Validate file size before upload (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          setBookingError(`File "${file.name}" is too large. Maximum size is 10MB.`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await safeFetch(`${API_URL}/api/upload/client-photo`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('client_token')}`
          },
          body: formData
        });
        
        // Read response body safely
        let data;
        let parseError = false;
        try {
          const text = await response.text();
          if (text) {
            data = JSON.parse(text);
          } else {
            data = { detail: 'Empty response from server' };
            parseError = true;
          }
        } catch (parseErr) {
          console.error('JSON parse error:', parseErr);
          data = { detail: 'Server returned invalid response' };
          parseError = true;
        }
        
        if (response.ok && !parseError && data.url) {
          const photoUrl = data.url || data.photo_url;
          setBookingForm(prev => ({
            ...prev,
            sitePhotos: [...prev.sitePhotos, photoUrl]
          }));
        } else {
          const errorMsg = data.detail || `Failed to upload "${file.name}"`;
          setBookingError(errorMsg);
          console.error('Upload error:', errorMsg, 'Status:', response.status);
        }
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      setBookingError('Network error during upload. Please check your connection and try again.');
    }
    
    setIsUploadingPhoto(false);
    e.target.value = ''; // Reset input
  };

  const removeBookingPhoto = (index) => {
    setBookingForm(prev => ({
      ...prev,
      sitePhotos: prev.sitePhotos.filter((_, i) => i !== index)
    }));
  };

  // Open new booking modal
  const openBookingModal = () => {
    setBookingForm({
      siteAddress: '',
      customerName: '',
      customerMobile: '',
      siteNotes: '',
      hasPallets: false,
      palletCount: '',
      sitePhotos: []
    });
    setBookingStep(0);
    setBookingError('');
    setBookingSuccess(null);
    setIsUploadingPhoto(false);
    setShowBookingModal(true);
  };

  // Submit booking
  const handleSubmitBooking = async () => {
    if (!bookingForm.siteAddress) {
      setBookingError('Please enter a site address');
      return;
    }
    if (!bookingForm.customerName || !bookingForm.customerMobile) {
      setBookingError('Please enter customer name and mobile');
      return;
    }

    setIsSubmittingBooking(true);
    setBookingError('');

    try {
      const requestBody = {
        site_address: bookingForm.siteAddress,
        customer_name: bookingForm.customerName,
        customer_mobile: bookingForm.customerMobile,
        site_notes: bookingForm.siteNotes || '',
        has_pallets: bookingForm.hasPallets || false,
        pallet_count: bookingForm.hasPallets ? parseInt(bookingForm.palletCount) || 0 : 0,
        site_photos: bookingForm.sitePhotos || []
      };

      // Use safeFetch to avoid body stream issues with Emergent script
      const response = await safeFetch(`${API_URL}/api/client-portal/create-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(requestBody)
      });

      const data = await safeParseResponse(response);

      if (response.ok) {
        setBookingSuccess(data);
        setBookingStep(1);
        // Refresh bookings and quota
        await Promise.all([fetchBookings(), fetchClientData(localStorage.getItem('client_token'))]);
      } else {
        setBookingError(data.detail || `Booking failed (${response.status})`);
      }
    } catch (err) {
      console.error('Booking submission error:', err);
      setBookingError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (token) {
      fetchClientData(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('client_token')}`
  });

  const fetchClientData = async (token) => {
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok) {
        setClient(data.client);
        setQuota(data.quota);
        setIsLoggedIn(true);
        setView('dashboard');
        
        // Fetch bookings, users, and activity
        await Promise.all([
          fetchBookings(), 
          fetchAuthorizedUsers(),
          fetchActivityHistory(data.client.mobile)
        ]);
      } else {
        localStorage.removeItem('client_token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('client_token');
    }
    setIsLoading(false);
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());
      
      const response = await safeFetch(`${API_URL}/api/client-portal/my-bookings?${params.toString()}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await safeParseResponse(response);
        // Handle paginated response
        if (data.bookings) {
          setBookings(data.bookings);
          setTotalBookings(data.total || 0);
        } else if (Array.isArray(data)) {
          // Fallback for non-paginated response
          setBookings(data);
          setTotalBookings(data.length);
        }
      }
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
  };

  // Re-fetch bookings when pagination changes
  useEffect(() => {
    if (isLoggedIn) {
      fetchBookings();
    }
  }, [currentPage, pageSize, isLoggedIn]);

  const fetchAuthorizedUsers = async () => {
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/my-authorized-users`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await safeParseResponse(response);
        setAuthorizedUsers(data.authorized_users || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchActivityHistory = async (mobile) => {
    setLoadingActivity(true);
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/my-activity`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await safeParseResponse(response);
        setActivityHistory(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchBookingActivity = async (bookingId) => {
    setLoadingBookingActivity(true);
    setBookingActivity({ status_history: [], activity_logs: [] });
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/booking/${bookingId}/activity`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await safeParseResponse(response);
        setBookingActivity({
          status_history: data.status_history || [],
          activity_logs: data.activity_logs || []
        });
      }
    } catch (err) {
      console.error('Failed to fetch booking activity:', err);
    } finally {
      setLoadingBookingActivity(false);
    }
  };

  // Handle booking selection to fetch activity
  const handleSelectBooking = (booking) => {
    setSelectedBooking(booking);
    fetchBookingActivity(booking.id);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);
    
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: loginForm.mobile,
          password: loginForm.password
        })
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok) {
        localStorage.setItem('client_token', data.access_token);
        await fetchClientData(data.access_token);
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setIsLoggingIn(false);
  };

  const handleRequestOTP = async () => {
    setError('');
    setIsSendingOtp(true);
    
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/request-password-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: passwordForm.mobile })
      });
      
      // Check for empty response
      const text = await response.text();
      if (!text) {
        setError('Server error. Please try again.');
        setIsSendingOtp(false);
        return;
      }
      
      const data = JSON.parse(text);
      
      if (response.ok && data.success) {
        setOtpSent(true);
      } else {
        setError(data.detail || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('OTP request error:', err);
      setError('Connection error. Please try again.');
    }
    setIsSendingOtp(false);
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setIsSettingPassword(true);
    
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: passwordForm.mobile,
          otp: passwordForm.otp,
          password: passwordForm.password
        })
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok) {
        alert('Password set successfully! You can now login.');
        setView('login');
        setLoginForm({ mobile: passwordForm.mobile, password: '' });
        setPasswordForm({ mobile: '', otp: '', password: '', confirmPassword: '' });
        setOtpSent(false);
      } else {
        setError(data.detail || 'Failed to set password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setIsSettingPassword(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    setIsLoggedIn(false);
    setClient(null);
    setView('login');
    setLoginForm({ mobile: '', password: '' });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setIsAddingUser(true);
    
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/my-authorized-users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(newUserForm)
      });
      
      const data = await safeParseResponse(response);
      
      if (response.ok) {
        alert(`${newUserForm.name} has been added as an authorized user`);
        setShowAddUserModal(false);
        setNewUserForm({ name: '', mobile: '', email: '', role: 'installer' });
        await fetchAuthorizedUsers();
      } else {
        setError(data.detail || 'Failed to add user');
      }
    } catch (err) {
      setError('Failed to add user');
    }
    setIsAddingUser(false);
  };

  const handleToggleUser = async (userId, userName) => {
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/my-authorized-users/${userId}/toggle`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const data = await safeParseResponse(response);
        alert(data.message);
        await fetchAuthorizedUsers();
      }
    } catch (err) {
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName}?`)) return;
    
    try {
      const response = await safeFetch(`${API_URL}/api/client-portal/my-authorized-users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        alert(`${userName} has been removed`);
        await fetchAuthorizedUsers();
      }
    } catch (err) {
      alert('Failed to remove user');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Australia/Melbourne'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Australia/Melbourne'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate expected pickup date based on booking creation and service level
  const getExpectedPickupDate = (booking) => {
    if (!booking || !booking.created_at) return null;
    
    // If already scheduled, use that date
    if (booking.scheduled_date) {
      return new Date(booking.scheduled_date);
    }
    
    // If completed or collected, no expected date needed
    if (['collected', 'completed', 'cancelled'].includes(booking.external_status)) {
      return null;
    }
    
    // Calculate based on service level SLA (default 7 days / 168 hours for standard)
    // Priority is 48 hours, Express is 24 hours
    let slaHours = 168; // Default 7 days
    const levelName = (booking.service_level_name || '').toLowerCase();
    
    if (levelName.includes('priority') || levelName.includes('48')) {
      slaHours = 48;
    } else if (levelName.includes('express') || levelName.includes('24')) {
      slaHours = 24;
    } else if (levelName.includes('subscription')) {
      slaHours = 168; // Weekly subscription = 7 days
    }
    
    const createdDate = new Date(booking.created_at);
    const expectedDate = new Date(createdDate.getTime() + (slaHours * 60 * 60 * 1000));
    
    return expectedDate;
  };

  const formatExpectedPickup = (booking) => {
    const expectedDate = getExpectedPickupDate(booking);
    if (!expectedDate) return null;
    
    const day = expectedDate.getDate();
    const month = expectedDate.toLocaleString('en-AU', { month: 'short', timeZone: 'Australia/Melbourne' });
    
    return `Expected to be picked up by ${day} ${month}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0D9488]" />
      </div>
    );
  }

  // Login View
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0FDFA] to-[#E0F2FE] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D9488] flex items-center justify-center">
              <Package className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#111111]">Client Portal</CardTitle>
            <p className="text-[#64748B] text-sm">Manage your bookings and team</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Mobile Number</label>
                <Input
                  type="tel"
                  value={loginForm.mobile}
                  onChange={(e) => setLoginForm({ ...loginForm, mobile: e.target.value })}
                  placeholder="0412 345 678"
                  required
                  data-testid="login-mobile"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Password</label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white"
                disabled={isLoggingIn}
                data-testid="login-submit"
              >
                {isLoggingIn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Login
              </Button>
            </form>
            
            <div className="mt-4 text-center space-y-2">
              <button 
                onClick={() => {
                  setPasswordForm({ mobile: '', otp: '', password: '', confirmPassword: '' });
                  setOtpSent(false);
                  setError('');
                  setView('set-password');
                }}
                className="text-[#0D9488] hover:underline text-sm block w-full"
              >
                First time? Set up your password
              </button>
              <button 
                onClick={() => {
                  setPasswordForm({ mobile: loginForm.mobile || '', otp: '', password: '', confirmPassword: '' });
                  setOtpSent(false);
                  setError('');
                  setView('forgot-password');
                }}
                className="text-[#64748B] hover:text-[#0D9488] hover:underline text-sm block w-full"
              >
                Forgot password?
              </button>
            </div>
            
            <div className="mt-6 pt-4 border-t border-[#E2E8F0] text-center">
              <a href="/" className="text-[#64748B] hover:text-[#0D9488] text-sm flex items-center justify-center gap-1">
                <Home className="w-4 h-4" /> Back to Transa
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Set Password View
  if (view === 'set-password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0FDFA] to-[#E0F2FE] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D9488] flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#111111]">Set Your Password</CardTitle>
            <p className="text-[#64748B] text-sm">For subscription clients only</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Mobile Number</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    value={passwordForm.mobile}
                    onChange={(e) => setPasswordForm({ ...passwordForm, mobile: e.target.value })}
                    placeholder="0412 345 678"
                    required
                    disabled={otpSent}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleRequestOTP}
                    disabled={!passwordForm.mobile || isSendingOtp || otpSent}
                    variant={otpSent ? "outline" : "default"}
                    className={otpSent ? "" : "bg-[#0D9488] hover:bg-[#0F766E]"}
                  >
                    {isSendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? 'Sent ✓' : 'Send OTP'}
                  </Button>
                </div>
              </div>
              
              {otpSent && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Verification Code</label>
                    <Input
                      type="text"
                      value={passwordForm.otp}
                      onChange={(e) => setPasswordForm({ ...passwordForm, otp: e.target.value })}
                      placeholder="Enter 6-digit code"
                      required
                      maxLength={6}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">New Password</label>
                    <Input
                      type="password"
                      value={passwordForm.password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Confirm Password</label>
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white"
                    disabled={isSettingPassword}
                  >
                    {isSettingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Set Password
                  </Button>
                </>
              )}
            </form>
            
            <div className="mt-4 text-center">
              <button 
                onClick={() => { setView('login'); setOtpSent(false); setError(''); }}
                className="text-[#0D9488] hover:underline text-sm"
              >
                Already have a password? Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password View
  if (view === 'forgot-password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0FDFA] to-[#E0F2FE] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#F59E0B] flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-[#111111]">Reset Password</CardTitle>
            <p className="text-[#64748B] text-sm">We'll send a code to verify your identity</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Mobile Number</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    value={passwordForm.mobile}
                    onChange={(e) => setPasswordForm({ ...passwordForm, mobile: e.target.value })}
                    placeholder="0412 345 678"
                    required
                    disabled={otpSent}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleRequestOTP}
                    disabled={!passwordForm.mobile || isSendingOtp || otpSent}
                    variant={otpSent ? "outline" : "default"}
                    className={otpSent ? "" : "bg-[#F59E0B] hover:bg-[#D97706]"}
                  >
                    {isSendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? 'Sent ✓' : 'Send Code'}
                  </Button>
                </div>
                {!otpSent && (
                  <p className="text-xs text-[#64748B] mt-1">Enter the mobile number linked to your account</p>
                )}
              </div>
              
              {otpSent && (
                <>
                  <div className="p-3 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-lg">
                    <p className="text-sm text-[#92400E]">
                      A 6-digit code has been sent to <strong>{passwordForm.mobile}</strong>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Verification Code</label>
                    <Input
                      type="text"
                      value={passwordForm.otp}
                      onChange={(e) => setPasswordForm({ ...passwordForm, otp: e.target.value })}
                      placeholder="Enter 6-digit code"
                      required
                      maxLength={6}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">New Password</label>
                    <Input
                      type="password"
                      value={passwordForm.password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                      placeholder="At least 6 characters"
                      required
                      minLength={6}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1">Confirm Password</label>
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Re-enter password"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white"
                    disabled={isSettingPassword}
                  >
                    {isSettingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Reset Password
                  </Button>
                  
                  <button 
                    type="button"
                    onClick={() => { setOtpSent(false); }}
                    className="w-full text-center text-sm text-[#64748B] hover:text-[#0D9488]"
                  >
                    Didn't receive the code? Try again
                  </button>
                </>
              )}
            </form>
            
            <div className="mt-4 text-center">
              <button 
                onClick={() => { setView('login'); setOtpSent(false); setError(''); }}
                className="text-[#0D9488] hover:underline text-sm"
              >
                ← Back to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0D9488] flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-[#111111]">{client?.business_name || client?.name}</h1>
              <p className="text-xs text-[#64748B] font-mono">
                {client?.client_ref || `SUB-${client?.id?.slice(0, 8).toUpperCase()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={openBookingModal}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white rounded-lg hover:bg-[#0F766E] transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Booking
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-[#64748B] hover:text-[#111111] hover:bg-[#F1F5F9] rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Quota Card */}
        {quota && (
          <Card className="mb-6 border-[#E2E8F0] overflow-hidden">
            <div className="bg-gradient-to-r from-[#0D9488] to-[#0F766E] px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Weekly Quota</h2>
                  <p className="text-[#CCFBF1] text-sm">{quota.week_start} - {quota.week_end}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{quota.bookings_remaining}</div>
                  <div className="text-[#CCFBF1] text-sm">bookings left</div>
                </div>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        quota.is_over_quota ? 'bg-red-500' : quota.bookings_remaining <= 1 ? 'bg-amber-500' : 'bg-[#0D9488]'
                      }`}
                      style={{ width: `${Math.min(100, (quota.bookings_used / quota.effective_limit) * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-sm text-[#64748B]">
                  {quota.bookings_used} / {quota.effective_limit} used
                </div>
              </div>
              {quota.bonus_bookings > 0 && (
                <p className="text-xs text-[#0D9488] mt-2">+{quota.bonus_bookings} bonus bookings this week</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F0FDFA] rounded-lg">
                  <Package className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111111]">{bookings.length}</p>
                  <p className="text-xs text-[#64748B]">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#DBEAFE] rounded-lg">
                  <Clock className="w-5 h-5 text-[#3B82F6]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111111]">
                    {bookings.filter(b => ['booking_received', 'scheduled'].includes(b.external_status)).length}
                  </p>
                  <p className="text-xs text-[#64748B]">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#FEF3C7] rounded-lg">
                  <Truck className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111111]">
                    {bookings.filter(b => b.external_status === 'on_the_way').length}
                  </p>
                  <p className="text-xs text-[#64748B]">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#DCFCE7] rounded-lg">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#111111]">
                    {bookings.filter(b => ['collected', 'completed'].includes(b.external_status)).length}
                  </p>
                  <p className="text-xs text-[#64748B]">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mobile New Booking Button */}
        <div className="sm:hidden mb-4">
          <button 
            onClick={openBookingModal}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[#0D9488] text-white rounded-lg hover:bg-[#0F766E] transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Booking
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#E2E8F0] overflow-x-auto">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'bookings' 
                ? 'border-[#0D9488] text-[#0D9488]' 
                : 'border-transparent text-[#64748B] hover:text-[#111111]'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('account')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'account' 
                ? 'border-[#0D9488] text-[#0D9488]' 
                : 'border-transparent text-[#64748B] hover:text-[#111111]'
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Account
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'users' 
                ? 'border-[#0D9488] text-[#0D9488]' 
                : 'border-transparent text-[#64748B] hover:text-[#111111]'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Team ({authorizedUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('support')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'support' 
                ? 'border-[#0D9488] text-[#0D9488]' 
                : 'border-transparent text-[#64748B] hover:text-[#111111]'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Support
          </button>
        </div>

        {/* My Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto text-[#94A3B8] mb-4" />
                  <h3 className="text-lg font-medium text-[#111111] mb-2">No bookings yet</h3>
                  <p className="text-[#64748B] mb-4">Start by creating your first waste collection booking</p>
                  <button 
                    onClick={openBookingModal}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white rounded-lg hover:bg-[#0F766E]"
                  >
                    <Plus className="w-4 h-4" />
                    New Booking
                  </button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-[#E2E8F0] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                        <th className="text-center py-3 px-3 text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12">#</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Booking</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Site Address</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Customer</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#64748B] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {bookings.map((booking, index) => {
                        const statusInfo = STATUS_COLORS[booking.external_status] || STATUS_COLORS.booking_received;
                        const rowNumber = (currentPage - 1) * pageSize + index + 1;
                        return (
                          <tr 
                            key={booking.id} 
                            className="hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                            onClick={() => handleSelectBooking(booking)}
                          >
                            <td className="py-3 px-3 text-center">
                              <span className="text-[#94A3B8] text-sm font-medium">{rowNumber}</span>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-semibold text-[#111111]">{booking.booking_ref}</p>
                                <p className="text-xs text-[#94A3B8]">
                                  {booking.service_level_name || booking.service_type || 'Standard'}
                                </p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-start gap-2 max-w-[250px]">
                                <MapPin className="w-4 h-4 text-[#94A3B8] mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-[#475569] truncate">
                                  {booking.waste_details?.site_address || '-'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="text-sm text-[#111111]">{booking.waste_details?.customer_name || '-'}</p>
                                <p className="text-xs text-[#94A3B8]">{booking.waste_details?.customer_mobile || ''}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="relative group inline-block">
                                <span 
                                  className="px-2.5 py-1 rounded-full text-xs font-medium inline-block cursor-help"
                                  style={{ backgroundColor: statusInfo.bg, color: statusInfo.text }}
                                >
                                  {statusInfo.label}
                                </span>
                                {/* Tooltip */}
                                {formatExpectedPickup(booking) && (
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-[#1E293B] text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                    {formatExpectedPickup(booking)}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                      <div className="border-4 border-transparent border-t-[#1E293B]"></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                {booking.scheduled_date ? (
                                  <>
                                    <p className="text-sm text-[#111111]">{formatDate(booking.scheduled_date)}</p>
                                    <p className="text-xs text-[#94A3B8]">{booking.scheduled_time_window || ''}</p>
                                  </>
                                ) : getExpectedPickupDate(booking) ? (
                                  <>
                                    <p className="text-sm text-[#64748B]">
                                      ~{getExpectedPickupDate(booking).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Melbourne' })}
                                    </p>
                                    <p className="text-xs text-[#94A3B8]">Expected</p>
                                  </>
                                ) : (
                                  <p className="text-sm text-[#94A3B8]">-</p>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectBooking(booking);
                                }}
                                className="p-2 text-[#0D9488] hover:bg-[#F0FDFA] rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                <div className="px-4 py-3 border-t border-[#E2E8F0] flex flex-col sm:flex-row items-center justify-between gap-3 bg-[#F8FAFC]">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#64748B]">
                      Showing {bookings.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} - {Math.min(currentPage * pageSize, totalBookings)} of {totalBookings} bookings
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#64748B]">Show:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        className="border border-[#E2E8F0] rounded-md px-2 py-1 text-sm bg-white text-[#111111]"
                      >
                        {pageSizeOptions.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-2"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {(() => {
                        const totalPages = Math.ceil(totalBookings / pageSize);
                        const pages = [];
                        let startPage = Math.max(1, currentPage - 2);
                        let endPage = Math.min(totalPages, startPage + 4);
                        if (endPage - startPage < 4) {
                          startPage = Math.max(1, endPage - 4);
                        }
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <Button
                              key={i}
                              variant={i === currentPage ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(i)}
                              className={`px-3 ${i === currentPage ? 'bg-[#0D9488] hover:bg-[#0B7A6E]' : ''}`}
                            >
                              {i}
                            </Button>
                          );
                        }
                        return pages;
                      })()}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalBookings / pageSize), p + 1))}
                      disabled={currentPage >= Math.ceil(totalBookings / pageSize)}
                      className="px-2"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.ceil(totalBookings / pageSize))}
                      disabled={currentPage >= Math.ceil(totalBookings / pageSize)}
                      className="px-2"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            {/* Client Reference and Status */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xl font-mono font-bold text-[#111111]">
                {client?.client_ref || `SUB-${client?.id?.slice(0, 8).toUpperCase()}`}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                client?.status === 'active' 
                  ? 'bg-[#DCFCE7] text-[#10B981]' 
                  : 'bg-[#FEE2E2] text-[#EF4444]'
              }`}>
                {client?.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              {client?.legal_agreements && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Terms Accepted
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Key Contact */}
              <Card className="border-[#E2E8F0]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-[#111111]">
                    <User className="w-4 h-4 text-[#0D9488]" /> Key Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xl font-semibold text-[#111111]">{client?.name}</p>
                  </div>
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <Phone className="w-4 h-4 text-[#0D9488]" />
                    <span>{client?.mobile}</span>
                  </div>
                  {client?.email && (
                    <div className="flex items-center gap-2 text-[#64748B]">
                      <Mail className="w-4 h-4 text-[#0D9488]" />
                      <span>{client?.email}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Company Details */}
              <Card className="border-[#E2E8F0]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-[#111111]">
                    <Building2 className="w-4 h-4 text-[#0D9488]" /> Company Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xl font-semibold text-[#111111]">{client?.business_name || '-'}</p>
                  </div>
                  {client?.abn && (
                    <div className="flex items-center gap-2 text-[#64748B]">
                      <Hash className="w-4 h-4 text-[#0D9488]" />
                      <span>ABN: {client?.abn}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Accounts Contact (if different) */}
            {client?.accounts_contact && !client?.accounts_contact?.same_as_key_contact && (
              <Card className="border-[#E2E8F0]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-[#111111]">
                    <Mail className="w-4 h-4 text-[#0D9488]" /> Accounts Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#64748B]">Name</p>
                      <p className="font-medium text-[#111111]">
                        {client.accounts_contact.first_name} {client.accounts_contact.last_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Email</p>
                      <p className="font-medium text-[#111111]">{client.accounts_contact.email}</p>
                    </div>
                    {client.accounts_contact.phone && (
                      <div>
                        <p className="text-sm text-[#64748B]">Phone</p>
                        <p className="font-medium text-[#111111]">{client.accounts_contact.phone}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subscription Details */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="border-[#E2E8F0] bg-gradient-to-br from-[#F0FDFA] to-white">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-[#64748B] mb-2">
                    <Package className="w-4 h-4" />
                    <span className="text-sm">Weekly Quota</span>
                  </div>
                  <p className="text-3xl font-bold text-[#0D9488]">
                    {quota?.effective_limit || client?.bookings_per_week || 5}
                    <span className="text-sm font-normal text-[#64748B] ml-1">/ week</span>
                  </p>
                  {quota?.bonus_bookings > 0 && (
                    <p className="text-xs text-green-600 mt-1">+{quota.bonus_bookings} bonus this week</p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-[#E2E8F0] bg-gradient-to-br from-[#FEF3C7] to-white">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-[#64748B] mb-2">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm">Weekly Cost</span>
                  </div>
                  <p className="text-3xl font-bold text-[#D97706]">
                    ${client?.weekly_cost || 45}
                    <span className="text-sm font-normal text-[#64748B] ml-1">/ week</span>
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Legal Agreement */}
            {client?.legal_agreements && (
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-purple-800">
                    <FileText className="w-4 h-4" /> Legal Agreement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-purple-800">
                      <span className="font-medium">Agreed on:</span>{' '}
                      {formatDateTime(client.legal_agreements.agreed_at)}
                    </p>
                    {client.legal_agreements.version && (
                      <p className="text-purple-600 text-sm">Version: {client.legal_agreements.version}</p>
                    )}
                    <div className="flex items-center gap-4 pt-2">
                      {client.legal_agreements.general_terms && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" /> General Terms
                        </span>
                      )}
                      {client.legal_agreements.service_terms && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" /> Service Terms
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity History */}
            <Card className="border-[#E2E8F0]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-[#111111]">
                  <History className="w-4 h-4 text-[#64748B]" /> Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0D9488]" />
                  </div>
                ) : activityHistory.length === 0 ? (
                  <p className="text-center text-[#94A3B8] py-8">No activity recorded yet</p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {activityHistory.slice(0, 20).map((activity, index) => (
                      <div key={index} className="flex gap-3 text-sm border-l-2 border-[#E2E8F0] pl-3 py-1">
                        <div className="flex-1">
                          <p className="text-[#111111]">{activity.description}</p>
                          <p className="text-xs text-[#94A3B8] mt-1">
                            {formatDateTime(activity.created_at)}
                          </p>
                        </div>
                        <span className={`h-fit px-2 py-0.5 rounded text-xs font-medium ${
                          activity.event_type?.includes('booking') ? 'bg-blue-100 text-blue-700' :
                          activity.event_type?.includes('otp') ? 'bg-amber-100 text-amber-700' :
                          activity.event_type?.includes('payment') ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {activity.event_type?.replace(/_/g, ' ') || 'activity'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Meta Info */}
            <p className="text-xs text-[#94A3B8] text-center">
              Member since {client?.created_at ? formatDate(client.created_at) : 'Unknown'} • ID: {client?.id}
            </p>
          </div>
        )}

        {/* Authorized Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-[#64748B] text-sm">
                Authorized users can book waste collection on your behalf using their mobile number.
              </p>
              <Button 
                onClick={() => setShowAddUserModal(true)}
                className="bg-[#0D9488] hover:bg-[#0F766E]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
            
            {authorizedUsers.length === 0 ? (
              <Card className="border-[#E2E8F0]">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-[#94A3B8] mb-4" />
                  <h3 className="text-lg font-medium text-[#111111] mb-2">No authorized users yet</h3>
                  <p className="text-[#64748B] mb-4">Add your team members so they can book collections on your behalf</p>
                  <Button 
                    onClick={() => setShowAddUserModal(true)}
                    className="bg-[#0D9488] hover:bg-[#0F766E]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First User
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {authorizedUsers.map((user) => (
                  <Card key={user.id} className={`border-[#E2E8F0] ${!user.is_active ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.is_active ? 'bg-[#F0FDFA]' : 'bg-[#F1F5F9]'
                        }`}>
                          <User className={`w-5 h-5 ${user.is_active ? 'text-[#0D9488]' : 'text-[#94A3B8]'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#111111]">{user.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'supervisor' ? 'bg-purple-100 text-purple-700' :
                              user.role === 'admin' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role}
                            </span>
                            {!user.is_active && (
                              <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full text-xs">
                                Inactive
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-[#64748B]">
                            <Phone className="w-3 h-3 inline mr-1" />
                            {user.mobile}
                            {user.email && (
                              <>
                                <span className="mx-2">•</span>
                                <Mail className="w-3 h-3 inline mr-1" />
                                {user.email}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleUser(user.id, user.name)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_active 
                              ? 'text-[#0D9488] hover:bg-[#F0FDFA]' 
                              : 'text-[#94A3B8] hover:bg-[#F1F5F9]'
                          }`}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {user.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.name)}
                          className="p-2 text-[#94A3B8] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove user"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <Card className="border-[#E2E8F0]">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#111111] mb-4">Contact Support</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                  <Phone className="w-6 h-6 text-[#0D9488]" />
                  <div>
                    <p className="font-medium text-[#111111]">Phone</p>
                    <a href="tel:+61407502587" className="text-[#0D9488] hover:underline">+61 407 502 587</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                  <Mail className="w-6 h-6 text-[#0D9488]" />
                  <div>
                    <p className="font-medium text-[#111111]">Email</p>
                    <a href="mailto:hello@transa.com.au" className="text-[#0D9488] hover:underline">hello@transa.com.au</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-lg">
                  <Clock className="w-6 h-6 text-[#0D9488]" />
                  <div>
                    <p className="font-medium text-[#111111]">Business Hours</p>
                    <p className="text-[#64748B]">Monday - Friday, 8am - 6pm AEST</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Booking Detail Drawer */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-start justify-end">
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl animate-in slide-in-from-right">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-[#111111]">{selectedBooking.booking_ref}</h2>
                <div className="relative group inline-block mt-1">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-sm font-medium cursor-help"
                    style={{ 
                      backgroundColor: STATUS_COLORS[selectedBooking.external_status]?.bg,
                      color: STATUS_COLORS[selectedBooking.external_status]?.text
                    }}
                  >
                    {STATUS_COLORS[selectedBooking.external_status]?.label || 'Processing'}
                  </span>
                  {/* Tooltip */}
                  {formatExpectedPickup(selectedBooking) && (
                    <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-[#1E293B] text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      {formatExpectedPickup(selectedBooking)}
                      <div className="absolute top-full left-4 transform -mt-1">
                        <div className="border-4 border-transparent border-t-[#1E293B]"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={() => { setSelectedBooking(null); setSelectedPhoto(null); }} className="text-[#94A3B8] hover:text-[#111111]">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Collection Address */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Collection Address
                </p>
                <p className="text-[#111111] font-medium">{selectedBooking.waste_details?.site_address}</p>
              </div>
              
              {/* Customer & Schedule */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Customer
                  </p>
                  <p className="text-[#111111] font-medium">{selectedBooking.waste_details?.customer_name}</p>
                  <p className="text-[#64748B] text-sm">{selectedBooking.waste_details?.customer_mobile}</p>
                </div>
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Scheduled
                  </p>
                  <p className="text-[#111111] font-medium">{formatDate(selectedBooking.scheduled_date) || 'TBD'}</p>
                  <p className="text-[#64748B] text-sm">{selectedBooking.scheduled_time_window || '-'}</p>
                </div>
              </div>
              
              {/* Service Info */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-2">Service Type</p>
                <div className="flex items-center gap-2">
                  <p className="text-[#111111] font-medium">
                    {selectedBooking.service_level_name || selectedBooking.service_type || 'Waste Collection'}
                  </p>
                  {selectedBooking.is_subscription_booking && (
                    <span className="px-2 py-0.5 bg-[#0D9488] text-white text-xs rounded-full">
                      Subscription
                    </span>
                  )}
                </div>
              </div>
              
              {/* Site Notes */}
              {selectedBooking.waste_details?.site_notes && (
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-2">Site Notes</p>
                  <p className="text-[#111111]">{selectedBooking.waste_details.site_notes}</p>
                </div>
              )}
              
              {/* Ops Notes */}
              {selectedBooking.ops_notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-700 text-sm mb-2">Updates</p>
                  <p className="text-amber-800">{selectedBooking.ops_notes}</p>
                </div>
              )}
              
              {/* Site Photos (uploaded by client during booking) */}
              {selectedBooking.site_photos && selectedBooking.site_photos.length > 0 && (
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Site Photos ({selectedBooking.site_photos.length})
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedBooking.site_photos.map((photo, idx) => {
                      const filename = getPhotoFilename(photo);
                      const viewLink = getPhotoViewLink(photo);
                      const isGoogleDrive = typeof photo === 'object' && photo.storage === 'google_drive';
                      
                      return (
                        <div key={idx} className="group">
                          <div 
                            className="relative cursor-pointer aspect-square bg-gray-100 rounded-lg overflow-hidden border border-[#E2E8F0]"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img 
                              src={getPhotoUrl(photo)} 
                              alt={filename}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            {isGoogleDrive && (
                              <div className="absolute bottom-1 left-1 p-1 bg-white/90 rounded text-xs flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* Filename */}
                          <div className="mt-1.5">
                            {viewLink ? (
                              <a 
                                href={viewLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-[#0D9488] hover:underline truncate block"
                                title={filename}
                              >
                                {filename}
                              </a>
                            ) : (
                              <p className="text-xs text-[#64748B] truncate" title={filename}>
                                {filename}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Proof of Collection Photos (uploaded by admin) */}
              {selectedBooking.proof_files && selectedBooking.proof_files.length > 0 && (
                <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                  <p className="text-[#64748B] text-sm mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Proof of Collection ({selectedBooking.proof_files.length})
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedBooking.proof_files.map((photo, idx) => {
                      const filename = getPhotoFilename(photo);
                      const viewLink = getPhotoViewLink(photo);
                      const isGoogleDrive = typeof photo === 'object' && photo.storage === 'google_drive';
                      
                      return (
                        <div key={idx} className="group">
                          <div 
                            className="relative cursor-pointer aspect-square bg-gray-100 rounded-lg overflow-hidden border border-[#E2E8F0]"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img 
                              src={getPhotoUrl(photo)} 
                              alt={filename}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            {isGoogleDrive && (
                              <div className="absolute bottom-1 left-1 p-1 bg-white/90 rounded text-xs flex items-center gap-1">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                                  <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                                </svg>
                              </div>
                            )}
                          </div>
                          {/* Filename */}
                          <div className="mt-1.5">
                            {viewLink ? (
                              <a 
                                href={viewLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-[#0D9488] hover:underline truncate block"
                                title={filename}
                              >
                                {filename}
                              </a>
                            ) : (
                              <p className="text-xs text-[#64748B] truncate" title={filename}>
                                {filename}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Activity History Section */}
              <div className="bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]">
                <p className="text-[#64748B] text-sm mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Activity History
                </p>
                
                {loadingBookingActivity ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-[#0D9488]" />
                  </div>
                ) : bookingActivity.status_history.length === 0 && bookingActivity.activity_logs.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm">No activity recorded yet</p>
                ) : (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {/* Combine and sort status history and activity logs */}
                    {[
                      ...bookingActivity.status_history.map(h => ({
                        type: 'status',
                        timestamp: h.timestamp,
                        description: `Status changed to ${h.new_status?.replace(/_/g, ' ')}`,
                        user: h.changed_by
                      })),
                      ...bookingActivity.activity_logs.map(a => ({
                        type: 'activity',
                        timestamp: a.timestamp || a.created_at,
                        description: a.description || a.action,
                        user: a.user_email || a.user
                      }))
                    ]
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .slice(0, 10)
                      .map((item, idx) => (
                        <div key={idx} className="flex gap-3 text-sm border-l-2 border-[#E2E8F0] pl-3 py-1">
                          <div className="flex-1">
                            <p className="text-[#475569]">{item.description}</p>
                            <p className="text-xs text-[#94A3B8]">
                              {formatDateTime(item.timestamp)}
                              {item.user && ` • ${item.user}`}
                            </p>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              
              {/* Timestamps */}
              <div className="pt-4 border-t border-[#E2E8F0] space-y-1 text-xs text-[#94A3B8]">
                <p>Booked on {formatDateTime(selectedBooking.created_at)}</p>
                {selectedBooking.updated_at && (
                  <p>Last updated {formatDateTime(selectedBooking.updated_at)}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between border-b border-[#E2E8F0] sticky top-0 bg-white z-10">
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0D9488]" />
                {bookingStep === 0 ? 'New Collection Booking' : 'Booking Confirmed'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowBookingModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              {bookingStep === 0 ? (
                <div className="space-y-4">
                  {/* Quota info */}
                  {quota && (
                    <div className="p-3 bg-[#F0FDFA] border border-[#0D9488]/20 rounded-lg">
                      <p className="text-sm text-[#0F766E]">
                        <span className="font-semibold">{quota.bookings_remaining}</span> bookings remaining this week
                      </p>
                    </div>
                  )}

                  {bookingError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {bookingError}
                    </div>
                  )}

                  {/* Site Address with autocomplete */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-[#111111] mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Site Address *
                    </label>
                    <Input
                      type="text"
                      value={bookingForm.siteAddress}
                      onChange={(e) => {
                        setBookingForm(prev => ({ ...prev, siteAddress: e.target.value }));
                        handleAddressSearch(e.target.value);
                      }}
                      onFocus={() => bookingForm.siteAddress.length >= 3 && setShowAddressSuggestions(true)}
                      placeholder="Start typing address..."
                      className="border-[#E2E8F0]"
                    />
                    {/* Address suggestions dropdown */}
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {addressSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-[#F8FAFC] border-b border-[#E2E8F0] last:border-b-0"
                            onClick={() => selectAddress(suggestion)}
                          >
                            {suggestion.description}
                          </button>
                        ))}
                      </div>
                    )}
                    {isLoadingAddresses && (
                      <div className="absolute right-3 top-10">
                        <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" />
                      </div>
                    )}
                  </div>

                  {/* Customer Name */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">
                      <User className="w-4 h-4 inline mr-1" />
                      Site Contact Name *
                    </label>
                    <Input
                      type="text"
                      value={bookingForm.customerName}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, customerName: e.target.value }))}
                      placeholder="Person at site"
                      className="border-[#E2E8F0]"
                    />
                  </div>

                  {/* Customer Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Site Contact Mobile *
                    </label>
                    <Input
                      type="tel"
                      value={bookingForm.customerMobile}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, customerMobile: e.target.value }))}
                      placeholder="04XX XXX XXX"
                      className="border-[#E2E8F0]"
                    />
                  </div>

                  {/* Site Notes */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Site Notes <span className="text-[#94A3B8] font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={bookingForm.siteNotes}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, siteNotes: e.target.value }))}
                      placeholder="Access instructions, specific location on site, etc."
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm resize-none"
                      rows={3}
                    />
                  </div>

                  {/* Site Photos Upload */}
                  <div>
                    <label className="block text-sm font-medium text-[#111111] mb-2">
                      <Image className="w-4 h-4 inline mr-1" />
                      Site Photos <span className="text-[#94A3B8] font-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-[#64748B] mb-2">Upload photos of the waste to help us prepare for collection</p>
                    
                    {/* Photo preview grid */}
                    {bookingForm.sitePhotos.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {bookingForm.sitePhotos.map((photo, idx) => (
                          <div key={idx} className="relative group aspect-square">
                            <img 
                              src={photo.startsWith('http') ? photo : `${API_URL}${photo}`}
                              alt={`Site photo ${idx + 1}`}
                              className="w-full h-full object-cover rounded-lg border border-[#E2E8F0]"
                            />
                            <button
                              type="button"
                              onClick={() => removeBookingPhoto(idx)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upload button */}
                    <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#E2E8F0] rounded-lg cursor-pointer hover:border-[#0D9488] hover:bg-[#F0FDFA] transition-colors ${isUploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {isUploadingPhoto ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-[#0D9488]" />
                          <span className="text-sm text-[#64748B]">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 text-[#0D9488]" />
                          <span className="text-sm text-[#64748B]">Add Photos</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleBookingPhotoUpload}
                        className="hidden"
                        disabled={isUploadingPhoto}
                      />
                    </label>
                  </div>

                  {/* Pallets */}
                  <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-[#111111]">Pallets on site?</label>
                      <button
                        type="button"
                        onClick={() => setBookingForm(prev => ({ ...prev, hasPallets: !prev.hasPallets, palletCount: '' }))}
                        className={`w-12 h-6 rounded-full transition-colors ${bookingForm.hasPallets ? 'bg-[#0D9488]' : 'bg-[#E2E8F0]'}`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${bookingForm.hasPallets ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    {bookingForm.hasPallets && (
                      <div className="mt-3">
                        <label className="block text-xs text-[#64748B] mb-1">How many pallets?</label>
                        <Input
                          type="number"
                          min="1"
                          value={bookingForm.palletCount}
                          onChange={(e) => setBookingForm(prev => ({ ...prev, palletCount: e.target.value }))}
                          placeholder="Number of pallets"
                          className="border-[#E2E8F0]"
                        />
                      </div>
                    )}
                  </div>

                  {/* Submit button */}
                  <Button
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white h-12"
                    onClick={handleSubmitBooking}
                    disabled={isSubmittingBooking || !bookingForm.siteAddress || !bookingForm.customerName || !bookingForm.customerMobile}
                  >
                    {isSubmittingBooking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Submit Booking
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                /* Success step */
                <div className="text-center py-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#DCFCE7] rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#10B981]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111111] mb-2">Booking Created!</h3>
                  <p className="text-[#64748B] mb-4">Your collection has been scheduled</p>
                  
                  {bookingSuccess && (
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 text-left mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#64748B]">Reference</span>
                        <span className="font-semibold text-[#111111]">{bookingSuccess.booking_ref}</span>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-[#64748B]">Site</span>
                        <span className="text-sm text-[#111111] text-right max-w-[200px] truncate">{bookingForm.siteAddress}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#64748B]">Status</span>
                        <span className="px-2 py-1 bg-[#F1F5F9] text-[#64748B] rounded-full text-xs font-medium">Booking Received</span>
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] text-white"
                    onClick={() => setShowBookingModal(false)}
                  >
                    Done
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col">
            <img 
              src={getPhotoUrl(selectedPhoto)} 
              alt="Full size"
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            {/* File info bar */}
            <div className="mt-3 bg-white/95 rounded-lg p-3 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#111111] truncate">
                  {getPhotoFilename(selectedPhoto)}
                </p>
                {typeof selectedPhoto === 'object' && selectedPhoto.storage === 'google_drive' && (
                  <p className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L4.5 14H12L7.5 22L19.5 10H12L16.5 2H12Z" fill="#4285F4"/>
                    </svg>
                    Stored in Google Drive
                  </p>
                )}
              </div>
              {getPhotoViewLink(selectedPhoto) && (
                <a
                  href={getPhotoViewLink(selectedPhoto)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="px-3 py-2 bg-[#0D9488] text-white rounded-lg text-sm flex items-center gap-2 hover:bg-[#0F766E] transition-colors flex-shrink-0"
                >
                  <Eye className="w-4 h-4" /> Open in Drive
                </a>
              )}
            </div>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-3 -right-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="text-xl font-semibold text-[#111111]">Add Authorized User</h2>
              <button onClick={() => setShowAddUserModal(false)} className="text-[#94A3B8] hover:text-[#111111]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Full Name *</label>
                <Input
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Mobile Number *</label>
                <Input
                  type="tel"
                  value={newUserForm.mobile}
                  onChange={(e) => setNewUserForm({ ...newUserForm, mobile: e.target.value })}
                  placeholder="0412 345 678"
                  required
                />
                <p className="text-xs text-[#64748B] mt-1">This will be their unique identifier when booking</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Email (optional)</label>
                <Input
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Role</label>
                <select
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
                >
                  <option value="installer">Installer</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => setShowAddUserModal(false)} 
                  className="flex-1"
                  disabled={isAddingUser}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-[#0D9488] hover:bg-[#0F766E]"
                  disabled={isAddingUser || !newUserForm.name || !newUserForm.mobile}
                >
                  {isAddingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortal;
